/**
 * Username and Display Name Validation
 * Balances user creativity with appropriate content moderation
 */

// Basic profanity filter - common words that should be blocked
const PROFANITY_WORDS = new Set([
  // Basic profanity
  'fuck', 'shit', 'bitch', 'ass', 'damn', 'hell',
  'crap', 'piss', 'dick', 'cock', 'pussy', 'whore',
  'slut', 'fag', 'faggot', 'nigger', 'nigga', 'retard',
  'stupid', 'idiot', 'moron', 'dumb', 'gay', 'lesbian',
  
  // Variations and common misspellings
  'fuk', 'sh1t', 'b1tch', 'a55', 'd4mn', 'h3ll',
  'cr4p', 'p1ss', 'd1ck', 'c0ck', 'puss1', 'wh0re',
  'sl1t', 'f4g', 'f4gg0t', 'n1gger', 'n1gga', 'r3tard',
  'stup1d', '1diot', 'm0ron', 'dumb', 'g4y', 'l3sbian',
  
  // Common substitutions
  'f*ck', 'sh*t', 'b*tch', 'a*s', 'd*mn', 'h*ll',
  'cr*p', 'p*ss', 'd*ck', 'c*ck', 'p*ssy', 'wh*re',
  'sl*t', 'f*g', 'f*ggot', 'n*gger', 'n*gga', 'r*tard',
  'stup*d', '*diot', 'm*ron', 'd*mb', 'g*y', 'l*sbian',
  
  // Spaced out versions
  'f u c k', 's h i t', 'b i t c h', 'a s s', 'd a m n',
  'h e l l', 'c r a p', 'p i s s', 'd i c k', 'c o c k',
  
  // Leetspeak variations
  'f0ck', 'sh1t', 'b1tch', 'a55', 'd4mn', 'h3ll',
  'cr4p', 'p1ss', 'd1ck', 'c0ck', 'puss1', 'wh0re',
  'sl1t', 'f4g', 'f4gg0t', 'n1gger', 'n1gga', 'r3tard',
  'stup1d', '1diot', 'm0ron', 'dumb', 'g4y', 'l3sbian'
]);

// Additional offensive terms
const OFFENSIVE_TERMS = new Set([
  'kill', 'murder', 'death', 'suicide', 'bomb', 'terrorist',
  'nazi', 'hitler', 'stalin', 'rape', 'molest', 'pedophile',
  'incest', 'bestiality', 'necrophilia', 'cannibal', 'torture',
  'execution', 'genocide', 'holocaust', 'slavery', 'lynch'
]);

// Spam patterns
const SPAM_PATTERNS = [
  /(.)\1{4,}/, // Repeated characters (aaaaa, 11111, etc.)
  /(.)\1{2,}(.)\2{2,}/, // Multiple repeated patterns
  /^[0-9]+$/, // Only numbers
  /^[^a-zA-Z0-9]+$/, // Only special characters
  /(admin|moderator|staff|support|help|system)/i, // System terms
  /(test|demo|sample|example|temp|temporary)/i, // Test terms
  /(user|account|profile|member)/i, // Generic terms
];

export interface ValidationResult {
  isValid: boolean;
  error?: string;
  suggestions?: string[];
}

/**
 * Clean and normalize a username for validation
 */
function normalizeUsername(username: string): string {
  return username
    .toLowerCase()
    .trim()
    .replace(/[^a-zA-Z0-9]/g, '') // Remove special characters for profanity check
    .replace(/[0-9]/g, '0') // Replace numbers with 0 for pattern matching
    .replace(/[aeiou]/g, 'a'); // Replace vowels with 'a' for pattern matching
}

/**
 * Check if a username contains profanity
 */
function containsProfanity(username: string): boolean {
  const normalized = normalizeUsername(username);
  
  // Check exact matches
  if (PROFANITY_WORDS.has(normalized)) {
    return true;
  }
  
  // Check for profanity words within the username
  for (const word of PROFANITY_WORDS) {
    if (normalized.includes(word)) {
      return true;
    }
  }
  
  // Check for offensive terms
  for (const term of OFFENSIVE_TERMS) {
    if (normalized.includes(term.toLowerCase())) {
      return true;
    }
  }
  
  return false;
}

/**
 * Check for spam patterns
 */
function containsSpamPatterns(username: string): boolean {
  return SPAM_PATTERNS.some(pattern => pattern.test(username));
}

/**
 * Generate suggestions for invalid usernames
 */
function generateSuggestions(username: string): string[] {
  const suggestions: string[] = [];
  
  // Remove special characters
  const cleaned = username.replace(/[^a-zA-Z0-9]/g, '');
  if (cleaned !== username && cleaned.length >= 3) {
    suggestions.push(cleaned);
  }
  
  // Add numbers to make it unique
  for (let i = 1; i <= 3; i++) {
    suggestions.push(`${cleaned}${i}`);
  }
  
  // Add common suffixes
  const suffixes = ['user', 'pro', 'master', 'star', 'king', 'queen'];
  for (const suffix of suffixes) {
    if (cleaned.length + suffix.length <= 20) {
      suggestions.push(`${cleaned}${suffix}`);
    }
  }
  
  return suggestions.slice(0, 5); // Limit to 5 suggestions
}

/**
 * Validate a username
 */
export function validateUsername(username: string): ValidationResult {
  // Check if username is provided
  if (!username || username.trim().length === 0) {
    return {
      isValid: false,
      error: 'Username is required'
    };
  }
  
  const trimmed = username.trim();
  
  // Check length
  if (trimmed.length < 3) {
    return {
      isValid: false,
      error: 'Username must be at least 3 characters long'
    };
  }
  
  if (trimmed.length > 30) {
    return {
      isValid: false,
      error: 'Username must be no more than 30 characters long'
    };
  }
  
  // Check for valid characters
  const validCharPattern = /^[a-zA-Z0-9._-]+$/;
  if (!validCharPattern.test(trimmed)) {
    return {
      isValid: false,
      error: 'Username can only contain letters, numbers, underscores, hyphens, and periods',
      suggestions: generateSuggestions(trimmed)
    };
  }
  
  // Check for consecutive special characters
  if (/[._-]{2,}/.test(trimmed)) {
    return {
      isValid: false,
      error: 'Username cannot have consecutive special characters',
      suggestions: generateSuggestions(trimmed)
    };
  }
  
  // Check for leading/trailing special characters
  if (/^[._-]|[._-]$/.test(trimmed)) {
    return {
      isValid: false,
      error: 'Username cannot start or end with special characters',
      suggestions: generateSuggestions(trimmed)
    };
  }
  
  // Check for profanity
  if (containsProfanity(trimmed)) {
    return {
      isValid: false,
      error: 'Username contains inappropriate content',
      suggestions: generateSuggestions(trimmed)
    };
  }
  
  // Check for spam patterns
  if (containsSpamPatterns(trimmed)) {
    return {
      isValid: false,
      error: 'Username appears to be spam or inappropriate',
      suggestions: generateSuggestions(trimmed)
    };
  }
  
  return {
    isValid: true
  };
}

/**
 * Validate a display name (more lenient than username)
 */
export function validateDisplayName(displayName: string): ValidationResult {
  // Check if display name is provided
  if (!displayName || displayName.trim().length === 0) {
    return {
      isValid: false,
      error: 'Display name is required'
    };
  }
  
  const trimmed = displayName.trim();
  
  // Check length
  if (trimmed.length < 2) {
    return {
      isValid: false,
      error: 'Display name must be at least 2 characters long'
    };
  }
  
  if (trimmed.length > 50) {
    return {
      isValid: false,
      error: 'Display name must be no more than 50 characters long'
    };
  }
  
  // Check for profanity (more lenient for display names)
  if (containsProfanity(trimmed)) {
    return {
      isValid: false,
      error: 'Display name contains inappropriate content',
      suggestions: generateSuggestions(trimmed)
    };
  }
  
  // Check for excessive special characters
  const specialCharCount = (trimmed.match(/[^a-zA-Z0-9\s]/g) || []).length;
  if (specialCharCount > trimmed.length / 2) {
    return {
      isValid: false,
      error: 'Display name has too many special characters',
      suggestions: generateSuggestions(trimmed)
    };
  }
  
  return {
    isValid: true
  };
}

/**
 * Check if a username is available (not already taken)
 */
export async function isUsernameAvailable(username: string): Promise<boolean> {
  try {
    const response = await fetch('/api/username/check', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username })
    });
    
    if (!response.ok) {
      return false;
    }
    
    const result = await response.json();
    return result.available;
  } catch (error) {
    console.error('Error checking username availability:', error);
    return false;
  }
}

/**
 * Comprehensive username validation with availability check
 */
export async function validateUsernameWithAvailability(username: string): Promise<ValidationResult> {
  // First, validate the format
  const formatValidation = validateUsername(username);
  if (!formatValidation.isValid) {
    return formatValidation;
  }
  
  // Then, check availability
  const available = await isUsernameAvailable(username);
  if (!available) {
    return {
      isValid: false,
      error: 'Username is already taken',
      suggestions: generateSuggestions(username)
    };
  }
  
  return {
    isValid: true
  };
}
